import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, filter, Subscription } from 'rxjs';

import { environment } from 'src/environments/environment';

import { BroadcasterService } from 'src/app/core/services';
import { ChatwootSdkService } from './chatwoot-sdk.service';
import { ResellerContact, SupportApiService, SupportContext, SupportRouting } from './support-api.service';
import { SupportStorageService } from './support-storage.service';

export type PreLoginSupportType = 'chatwoot' | 'contact' | 'none';

@Injectable({
  providedIn: 'root',
})
export class SupportService implements OnDestroy {
  private contextLoaded = false;
  private sdkLoaded = false;
  private context: SupportContext | null = null;
  private routerSub: Subscription;
  private logoutSub: Subscription;
  private _available$ = new BehaviorSubject<boolean>(false);
  private _loading$ = new BehaviorSubject<boolean>(false);
  public available$ = this._available$.asObservable();
  public loading$ = this._loading$.asObservable();

  // Pre-login state
  private preLoginRouting: SupportRouting | null = null;
  private _preLoginSupportType$ = new BehaviorSubject<PreLoginSupportType>('none');
  private _preLoginReady$ = new BehaviorSubject<boolean>(false);
  public preLoginSupportType$ = this._preLoginSupportType$.asObservable();
  public preLoginReady$ = this._preLoginReady$.asObservable();

  constructor(
    private chatwootSdk: ChatwootSdkService,
    private supportApi: SupportApiService,
    private supportStorage: SupportStorageService,
    private broadcasterService: BroadcasterService,
    private router: Router,
  ) {
    this.logoutSub = this.broadcasterService.on('logout').subscribe(() => {
      this.destroy();
    });
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.logoutSub?.unsubscribe();
  }

  /**
   * Busca o contexto de suporte da API (sem carregar o SDK).
   * Se houver config Chatwoot, exibe o botão.
   */
  init(): void {
    if (this.contextLoaded) return;
    this.contextLoaded = true;

    this.supportApi.getContext().subscribe({
      next: (ctx: SupportContext) => {
        // Salva contexto no localStorage para uso pré-login (sempre)
        if (ctx.reseller?.id) {
          this.supportStorage.save({
            resellerId: ctx.reseller.id,
            organizationName: ctx.organization.name,
            unitHash: '',
            savedAt: '',
          });
        }

        if (!ctx.chatwoot.base_url || !ctx.chatwoot.website_token) {
          return;
        }

        this.context = ctx;
        this._available$.next(true);
      },
      error: (err) => {
        // Filtra ruído: 401 é esperado em /login/ (sem token);
        // 0/5xx são transientes (rede ou indisponibilidade do server) e
        // não acionáveis — falhar aqui só esconde o botão de suporte.
        // Erros HTTP inesperados são capturados pela integração HTTP do
        // Sentry SDK independentemente deste warn.
        const status = err?.status ?? 0;
        const isTransient = status === 0 || status >= 500;
        if (status !== 401 && !isTransient) {
          console.warn(
            `[SupportService] Failed to get support context: ${status} ${err?.message || ''}`.trim(),
          );
        }
        this.contextLoaded = false;
      },
    });
  }

  /**
   * Carrega o SDK sob demanda e abre o widget.
   * Chamado quando o usuário clica no botão de suporte.
   */
  openWidget(): void {
    if (!this.context) return;

    if (this.chatwootSdk.isLoaded) {
      this.chatwootSdk.setCustomAttributes({
        current_route: this.router.url,
        current_module: this.extractModule(this.router.url),
      });
      this.chatwootSdk.toggle('open');
      return;
    }

    if (this._loading$.getValue()) return;
    this._loading$.next(true);

    const { chatwoot, user, organization, unit, reseller } = this.context;

    this.chatwootSdk.loadSdk(chatwoot.base_url, chatwoot.website_token).then(() => {
      this.chatwootSdk.setUser(user.identifier, {
        name: user.name,
        email: user.email,
        phone_number: user.phone_number || undefined,
        company_name: organization.name,
        identifier_hash: user.identity_hash,
      });

      this.chatwootSdk.setCustomAttributes({
        organization_name: organization.name,
        organization_id: organization.id,
        unit_name: unit.name,
        unit_id: unit.id,
        reseller_name: reseller.name || 'N/A',
        current_route: this.router.url,
        current_module: this.extractModule(this.router.url),
        app_version: environment.version,
      });

      this.chatwootSdk.setLocale('pt_BR');

      this.sdkLoaded = true;
      this._loading$.next(false);
      this.listenRouteChanges();
      this.chatwootSdk.toggle('open');
    }).catch((err) => {
      console.error('[SupportService] Failed to load Chatwoot SDK:', err);
      this._loading$.next(false);
    });
  }

  /**
   * Verifica disponibilidade de suporte pré-login.
   * Chamado no ngOnInit do LoginComponent.
   */
  checkPreLoginSupport(): void {
    const savedContext = this.supportStorage.load();
    if (!savedContext?.resellerId) {
      this._preLoginSupportType$.next('none');
      this._preLoginReady$.next(true);
      return;
    }

    this.supportApi.getRouting({ reseller_id: String(savedContext.resellerId) }).subscribe({
      next: (routing: SupportRouting) => {
        this.preLoginRouting = routing;
        this._preLoginSupportType$.next(routing.support_type);
        this._preLoginReady$.next(true);
      },
      error: () => {
        this._preLoginSupportType$.next('none');
        this._preLoginReady$.next(true);
      },
    });
  }

  get preLoginContact(): ResellerContact | null {
    return this.preLoginRouting?.contact ?? null;
  }

  get preLoginResellerName(): string | null {
    return this.preLoginRouting?.reseller_name ?? null;
  }

  /**
   * Abre o widget Chatwoot na tela de login (sem autenticação).
   * Usa o routing já resolvido por checkPreLoginSupport().
   */
  openPreLoginWidget(): void {
    if (!this.preLoginRouting || this.preLoginRouting.support_type !== 'chatwoot') return;

    if (this.chatwootSdk.isLoaded) {
      this.chatwootSdk.toggle('open');
      return;
    }

    if (this._loading$.getValue()) return;
    this._loading$.next(true);

    const routing = this.preLoginRouting;
    const savedContext = this.supportStorage.load();

    this.chatwootSdk.loadSdk(routing.chatwoot_base_url!, routing.chatwoot_website_token!).then(() => {
      this.chatwootSdk.setCustomAttributes({
        reseller_name: routing.reseller_name || 'N/A',
        current_route: 'login',
        current_module: 'pre-login',
        app_version: environment.version,
        ...(savedContext?.organizationName ? { organization_name: savedContext.organizationName } : {}),
      });

      this.chatwootSdk.setLocale('pt_BR');
      this._loading$.next(false);
      this.chatwootSdk.toggle('open');
    }).catch((err) => {
      console.error('[SupportService] Failed to load Chatwoot SDK (pre-login):', err);
      this._loading$.next(false);
    });
  }

  destroy(): void {
    this.chatwootSdk.reset();
    this.routerSub?.unsubscribe();
    this.routerSub = null;
    this.contextLoaded = false;
    this.sdkLoaded = false;
    this.context = null;
    this._available$.next(false);
    this._loading$.next(false);
    this.preLoginRouting = null;
    this._preLoginSupportType$.next('none');
    this._preLoginReady$.next(false);
  }

  private listenRouteChanges(): void {
    if (this.routerSub) return;

    this.routerSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.chatwootSdk.setCustomAttributes({
          current_route: event.urlAfterRedirects,
          current_module: this.extractModule(event.urlAfterRedirects),
        });
      });
  }

  private extractModule(url: string): string {
    const segments = url.split('/').filter(Boolean);
    return segments[0] || 'home';
  }
}
